import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, Minus, ArrowRightLeft, ArrowUp, ArrowDown, Repeat } from 'lucide-react';
import { dynastyTheme, withAlpha } from '../../../../../services/colorService';
import WatchButton from '../../common/WatchButton';

const C = dynastyTheme.tokens.colors;
const GOLD = dynastyTheme.tokens.colors.primary;
const FOOTER_BORDER = `1px solid ${C.border}`;

// One flat footer cell: line icon over a small label, left-divider between cells.
const FooterCell = ({ Icon, label, onClick, color = C.mutedLight, divider = true, disabled = false }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    style={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 3, padding: '8px 0', background: 'transparent', border: 'none',
      borderLeft: divider ? FOOTER_BORDER : 'none', cursor: disabled ? 'default' : 'pointer',
      color, opacity: disabled ? 0.35 : 1, WebkitTapHighlightColor: 'transparent',
    }}
  >
    <Icon size={17} strokeWidth={1.8} color={color} aria-hidden="true" />
    <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.3px', lineHeight: 1 }}>{label}</span>
  </button>
);

/**
 * QuickActionsBar Component
 *
 * Action buttons for player modal (Add, Drop, Trade, Watch, View Full).
 * Handles disabled states and provides visual feedback.
 * Includes confirmation dialog for DROP action.
 *
 * 9l.2: the Watch action is now rendered by mobile-internal WatchButton
 * (self-contained — owns its own state + API calls). onWatch + isWatched
 * props are no longer used and have been removed from the API.
 *
 * @component
 * @example
 * <QuickActionsBar
 *   playerId="545361"
 *   onAdd={() => console.log('Add clicked')}
 *   onDrop={() => console.log('Drop clicked')}
 *   onTrade={() => console.log('Trade clicked')}
 *   onViewFull={() => console.log('View Full clicked')}
 *   isOnRoster={false}
 * />
 */
const QuickActionsBar = ({
  playerId,
  onAdd,
  onDrop,
  onTrade,
  onSwap,
  onViewFull,
  isOnRoster = false,
  showActions = true,
  playerName = null,
  // The 4th cell: modal = "Full ↑" (expand to the full profile). The full
  // profile passes "Return ↓" (collapse back) — same handler slot, relabelled.
  viewFullLabel = 'Full',
  viewFullVariant = 'up',
  // DD-WAIVER-0723-BUILD W2 (Bible § 4.5.19): the Add cell is context-aware
  // for waiver states — 'Claim' (active hold, another team cut him) or
  // 'Cooling Off' (disabled — the viewer's team cut him). The parent
  // computes the state via getWaiverActionState and passes it down.
  addLabel = 'Add',
  addDisabled = false,
}) => {
  const [showDropConfirm, setShowDropConfirm] = useState(false);

  const handleConfirmDrop = () => {
    setShowDropConfirm(false);
    if (onDrop) onDrop(playerId);
  };
  const handleCancelDrop = () => setShowDropConfirm(false);

  // Locked Action bar — a flat DIVIDED footer (never chunky pills). Slot 1 is
  // context-aware: Add when the player is a free agent, Drop when on roster
  // (approved item 2). Trade · Watch · Full↑ follow. All handlers reused as-is.
  return (
    <>
      <div style={styles.bar}>
        {showActions && (
          <>
            {isOnRoster ? (
              <FooterCell Icon={Minus} label="Drop" color={dynastyTheme.tokens.colors.error} divider={false} onClick={() => setShowDropConfirm(true)} />
            ) : (
              <FooterCell Icon={Plus} label={addLabel} divider={false} disabled={addDisabled} onClick={() => onAdd && onAdd(playerId)} />
            )}
            {/* DD-RO-MOBILE-SWAP-0727: player-initiated 1-1 swap into MobileFASwapSheet (the mobile counterpart
                of the desktop field-view swap). FA → drop a roster player; roster player → replace with an FA. */}
            {onSwap && (
              <FooterCell Icon={Repeat} label="Swap" onClick={() => onSwap(playerId)} />
            )}
            <FooterCell Icon={ArrowRightLeft} label="Trade" onClick={() => onTrade && onTrade(playerId)} />
            {/* Watch — self-contained WatchButton, flattened to match the other
                cells (no filled box): transparent bg, no border, hairline divider. */}
            {playerId && (
              <WatchButton
                playerId={playerId}
                style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', borderRadius: 0, borderLeft: FOOTER_BORDER, padding: '8px 0', gap: 3 }}
              />
            )}
          </>
        )}
        {/* 4th cell — modal "Full ↑" (expand) / profile "Return ↓" (collapse) */}
        <FooterCell Icon={viewFullVariant === 'down' ? ArrowDown : ArrowUp} label={viewFullLabel} color={GOLD} divider={showActions} onClick={() => onViewFull && onViewFull(playerId)} />
      </div>

      {showDropConfirm && (
        <DropConfirmationDialog
          playerName={playerName}
          onConfirm={handleConfirmDrop}
          onCancel={handleCancelDrop}
        />
      )}
    </>
  );
};

/**
 * Drop Confirmation Dialog Component
 */
const DropConfirmationDialog = ({ playerName, onConfirm, onCancel }) => {
  const handleOverlayClick = (e) => {
    // Close dialog if clicking on overlay (not dialog content)
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const handleConfirm = (e) => {
    e.stopPropagation();
    onConfirm();
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    onCancel();
  };

  return (
    <div 
      style={confirmDialogStyles.overlay}
      onClick={handleOverlayClick}
    >
      <div 
        style={confirmDialogStyles.dialog}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={confirmDialogStyles.header}>
          <div style={confirmDialogStyles.icon}>⚠️</div>
          <div style={confirmDialogStyles.title}>Drop Player?</div>
        </div>
        <div style={confirmDialogStyles.message}>
          Are you sure you want to drop {playerName || 'this player'} from your roster?
          <div style={confirmDialogStyles.warning}>
            This action cannot be undone.
          </div>
        </div>
        <div style={confirmDialogStyles.actions}>
          <button
            onClick={handleCancel}
            style={confirmDialogStyles.cancelButton}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            style={confirmDialogStyles.confirmButton}
          >
            Drop Player
          </button>
        </div>
      </div>
    </div>
  );
};

DropConfirmationDialog.propTypes = {
  playerName: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

QuickActionsBar.propTypes = {
  /** Player ID */
  playerId: PropTypes.string.isRequired,
  
  /** Callback when Add button clicked */
  onAdd: PropTypes.func,
  
  /** Callback when Drop button clicked */
  onDrop: PropTypes.func,
  
  /** Callback when Trade button clicked */
  onTrade: PropTypes.func,

  /** Callback when View Full button clicked */
  onViewFull: PropTypes.func,

  /** Whether player is on user's roster */
  isOnRoster: PropTypes.bool,

  /** Whether to show action buttons (hide when not in league context) */
  showActions: PropTypes.bool,

  /** Player name for confirmation dialog */
  playerName: PropTypes.string,

  /** 4th-cell label ('Full' modal / 'Return' profile) + arrow variant */
  viewFullLabel: PropTypes.string,
  viewFullVariant: PropTypes.oneOf(['up', 'down']),

  /** W2 waiver-aware Add cell: label ('Add' | 'Claim' | 'Cooling Off') + disabled state */
  addLabel: PropTypes.string,
  addDisabled: PropTypes.bool
};

QuickActionsBar.defaultProps = {
  onAdd: null,
  onDrop: null,
  onTrade: null,
  onViewFull: null,
  isOnRoster: false,
  showActions: true
};

const styles = {
  bar: {
    display: 'flex',
    alignItems: 'stretch',
    background: C.surface[730],
    borderTop: FOOTER_BORDER,
    flexShrink: 0,
  },
};

const confirmDialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: withAlpha(C.black, 0.8),
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    animation: 'fadeIn 200ms ease-out'
  },

  dialog: {
    backgroundColor: dynastyTheme.tokens.colors.neutral[900],
    borderRadius: '12px',
    padding: '20px',
    maxWidth: '320px',
    width: '100%',
    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
    boxShadow: `0 8px 32px ${withAlpha(C.black, 0.5)}`
  },

  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px'
  },

  icon: {
    fontSize: '32px'
  },

  title: {
    fontSize: '18px',
    fontWeight: '700',
    color: dynastyTheme.tokens.colors.white
  },

  message: {
    fontSize: '14px',
    color: dynastyTheme.tokens.colors.white,
    textAlign: 'center',
    lineHeight: '1.5',
    marginBottom: '20px'
  },

  warning: {
    fontSize: '12px',
    color: dynastyTheme.tokens.colors.error,
    fontWeight: '600',
    marginTop: '8px'
  },

  actions: {
    display: 'flex',
    gap: '8px'
  },

  cancelButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: dynastyTheme.tokens.colors.neutral[700],
    border: 'none',
    borderRadius: '6px',
    color: dynastyTheme.tokens.colors.white,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 150ms ease',
    WebkitTapHighlightColor: 'transparent'
  },

  confirmButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: dynastyTheme.tokens.colors.error,
    border: 'none',
    borderRadius: '6px',
    color: dynastyTheme.tokens.colors.white,
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 150ms ease',
    WebkitTapHighlightColor: 'transparent'
  }
};

export default QuickActionsBar;
